import * as React from 'react'
import { Mail, Bell } from 'lucide-react'
import { useNotificationPreferences, useUpdateNotificationPreference } from '@/lib/queries.js'
import { Card, CardBody } from '@/components/ui/card.jsx'
import { Alert, Spinner } from '@/components/ui/feedback.jsx'
import { humanMessage } from '@/lib/errors.js'

/**
 * Notification preferences (Phase 2, item 19 — D28c).
 *
 * PER-TENANT-USER, not per-tenant (brief, verbatim) — this reads and writes
 * `req.user.id`'s own rows, the same person viewing the screen, matching the
 * `notification_preferences` table's own UNIQUE(user_id, notification_type)
 * shape.
 */

const TYPE_LABELS = {
  lead_arrived: { label: 'A new lead arrives', hint: 'Someone filled out your lead form.' },
  estimate_signed: { label: 'An estimate is signed', hint: 'A customer accepted and signed.' },
  payment_received: { label: 'A payment is recorded', hint: 'Cash, cheque, card, or anything else.' }
}

function Toggle ({ checked, onChange, label, disabled }) {
  return (
    <label className="flex min-h-tap cursor-pointer items-center gap-2 text-sm text-ink">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        disabled={disabled}
        className="size-4 rounded border-line accent-brand"
      />
      {label}
    </label>
  )
}

export function NotificationPreferencesCard () {
  const { data, isPending, error } = useNotificationPreferences()
  const update = useUpdateNotificationPreference()
  const [localError, setLocalError] = React.useState(null)

  if (isPending) {
    return <Card><CardBody className="flex justify-center py-8"><Spinner label="Loading notification settings" /></CardBody></Card>
  }
  if (error) return <Card><CardBody><Alert tone="danger">{humanMessage(error)}</Alert></CardBody></Card>

  async function toggle (type, channel, value) {
    setLocalError(null)
    try {
      await update.mutateAsync({ type, [channel]: value })
    } catch (err) { setLocalError(err) }
  }

  return (
    <Card>
      <CardBody className="space-y-4">
        {localError && <Alert tone="danger">{humanMessage(localError)}</Alert>}

        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="notification-preferences">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-ink-subtle">
                <th className="py-2 pr-3 font-medium">When</th>
                <th className="py-2 px-3 font-medium"><span className="flex items-center gap-1"><Bell className="size-3.5" aria-hidden="true" />In app</span></th>
                <th className="py-2 pl-3 font-medium"><span className="flex items-center gap-1"><Mail className="size-3.5" aria-hidden="true" />Email</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {data.map(pref => (
                <tr key={pref.notification_type}>
                  <td className="py-3 pr-3">
                    <p className="text-sm font-medium text-ink">{TYPE_LABELS[pref.notification_type]?.label ?? pref.notification_type}</p>
                    <p className="text-xs text-ink-muted">{TYPE_LABELS[pref.notification_type]?.hint}</p>
                  </td>
                  <td className="py-3 px-3">
                    <Toggle
                      checked={pref.push_enabled}
                      disabled={update.isPending}
                      onChange={v => toggle(pref.notification_type, 'push_enabled', v)}
                      label=""
                    />
                  </td>
                  <td className="py-3 pl-3">
                    <Toggle
                      checked={pref.email_enabled}
                      disabled={update.isPending}
                      onChange={v => toggle(pref.notification_type, 'email_enabled', v)}
                      label=""
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  )
}
